/** Overview Traffic / Requests time-series chart — a stacked area per CDN
 * platform from /waf/home/diagram. */
import { useMemo, useState } from 'react';
import { BarChart2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardBody, SegmentedControl, Skeleton } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Chart, type ChartSeries } from '@vnetwork/ui/charts';
import { useWafHomeDiagram, type WafHomeScope } from '@/api/waf';
import { intervalForRange, fmtAxisLabel } from '../analytics/diagram-transform';
import { fmtBytes, fmtNum } from './format';
import styles from './WafDashboard.module.css';

type CdnMetric = 'traffic' | 'requests';

/** Safe per-series row key from a platform label. */
const keyFor = (label: string, i: number) => `s${i}_${label.replace(/[^\w]/g, '_')}`;

const PALETTE = [
  'var(--vn-color-blue-400)',
  'var(--vn-color-green-400)',
  'var(--vn-color-amber-400)',
  'var(--vn-color-red-400)',
  'var(--vn-accent-solid)',
];

export function TrafficChartCard({ serviceId, scope }: { serviceId: string; scope: WafHomeScope }) {
  const [metric, setMetric] = useState<CdnMetric>('traffic');
  const metricKey = metric === 'traffic' ? 'traffic_bytes' : 'requests';
  const metricLabel = metric === 'traffic' ? 'Traffic' : 'Requests';
  const format = metric === 'traffic' ? fmtBytes : fmtNum;
  const q = useWafHomeDiagram(serviceId, scope, metricKey);

  const { rows, series, total } = useMemo(() => {
    const labels = q.data?.labels ?? [];
    const interval = intervalForRange(scope.startTime, scope.endTime);
    const lines = (q.data?.datasets ?? []).map((ds) => {
      const vals = ds.series?.[metricKey] ?? ds.series?.[Object.keys(ds.series ?? {})[0]] ?? [];
      return { label: ds.label, values: vals.map((v) => Number(v) || 0) };
    });
    const single = lines.length === 1;
    const ser: ChartSeries[] = lines.map((l, i) => ({
      key: keyFor(l.label, i),
      // Single series shows the metric name ("Traffic"/"Requests") instead of the
      // raw key; keep the dataset labels for multi-series legend.
      label: single ? metricLabel : l.label,
      color: single ? PALETTE[0] : PALETTE[i % PALETTE.length],
    }));
    const rws = labels.map((ms, i) => {
      const row: Record<string, unknown> = { t: fmtAxisLabel(ms, interval) };
      lines.forEach((l, li) => { row[ser[li].key] = l.values[i] ?? 0; });
      return row;
    });
    const tot = lines.reduce((a, l) => a + l.values.reduce((x, y) => x + y, 0), 0);
    return { rows: rws, series: ser, total: tot };
  }, [q.data, metricKey, metricLabel, scope.startTime, scope.endTime]);

  const hasData = rows.length > 0 && series.length > 0;

  return (
    <Card>
      <CardHeader
        actions={
          <SegmentedControl
            items={[{ value: 'traffic', label: 'Traffic' }, { value: 'requests', label: 'Requests' }]}
            value={metric}
            onValueChange={(v) => setMetric(v as CdnMetric)}
            size="sm"
            aria-label="Metric"
          />
        }
      >
        <CardTitle>{metric === 'traffic' ? 'Traffic' : 'Requests'}</CardTitle>
      </CardHeader>
      <CardBody>
        <p className={styles.chartTotal}>
          {metric === 'traffic' ? `Total bytes: ${fmtBytes(total)}` : `Total requests: ${fmtNum(total)}`}
        </p>
        {q.isPending ? (
          <Skeleton style={{ height: 240 }} />
        ) : hasData ? (
          <Chart
            type="area"
            data={rows}
            xKey="t"
            series={series}
            height={240}
            stacked
            showGrid
            showLegend={series.length > 1}
            valueFormatter={(v) => format(Number(v) || 0)}
          />
        ) : (
          <EmptyState icon={<BarChart2 size={20} />} title="No traffic data yet" description="No samples in the selected range." />
        )}
      </CardBody>
    </Card>
  );
}

export default TrafficChartCard;
